import { useParams } from 'react-router-dom';

import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import useFetchSessionStats, { ESessionStatKeys } from './use-fetch-session-stats';
import StatsCard from '../components/stats-card';
import IconFilterAltGradient from '@ui/icons/gradient-icons/icon-filter-alt-gradient';
import Link from '@ui/links/link';
import ViewStatsContent from '../components/view-stats-content';
import useGetStatsIconColors from '../helpers/use-get-stats-icon-colors';
import { EPermissions } from '@/types/permissions.types';
import AuthWrapper from '@ui/auth-wrapper/auth-wrapper';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { useAppSelector } from '@/store/hooks';
import { selectUserChannel } from '@/store/slices/user/selectors';
import { formatMinutesToHoursAndMinutes } from '@/utils/dates';
import OptionalComponent from '@ui/optional-component/optional-component';
import { useIsLoadingSessionStat } from './use-is-loading-session-stats';
import { ChannelType } from '@/types/channel.types';

type SessionStatsRowProps = {
  sessionUuid?: string;
  projectUuid?: string;
  isPublished?: boolean;
};

const SessionStatsRow = ({ projectUuid, isPublished, sessionUuid }: SessionStatsRowProps) => {
  const params = useParams();
  const language = params?.language || 'en';

  const userChannel = useAppSelector(selectUserChannel);

  const channelType = userChannel?.type || ChannelType.Webinar;

  const { data } = useFetchSessionStats({
    projectUuid,
    sessionUuid,
    disabled: !isPublished,
    language,
    stats: [
      ESessionStatKeys.avgUserWatchtime,
      ESessionStatKeys.averageProjectRating,
      channelType === ChannelType.Townhall ? ESessionStatKeys.interactions : ESessionStatKeys.shares,
    ],
  });

  const isLoadingSessionStat = useIsLoadingSessionStat(data, isPublished || false);

  const statsColors = useGetStatsIconColors();

  return (
    <Container data-component="SessionStatsRow" className="flex-column gap-8 padding-t-6" maxWidth="392px">
      <div className="flex ai-flex-end jc-space-between">
        <Text className="H5" design="GlobalIcoText1">
          Stats
        </Text>
        <div>
          <Container>
            <AuthWrapper allowedRoles={[EPermissions.GroupAdmin]}>
              <Link
                to={`/project/edit/${projectUuid}/${language}/stats/session/${sessionUuid}`}
                textColor="none"
                focusBackgroundColor="rgba(var(--gray-700-rgb), 0.5)"
                applyFocusStatesToAllChildren
                hoverOpacity={0.8}
              >
                <ViewStatsContent />
              </Link>
            </AuthWrapper>
          </Container>
        </div>
      </div>

      <Container className="flex fd-row fw-wrap jc-flex-start ai-center gap-8">
        <StatsCard
          loading={isLoadingSessionStat(ESessionStatKeys.avgUserWatchtime)}
          title="Watchtime"
          tooltip="Average duration of video content consumed by attendee"
          value={isPublished ? formatMinutesToHoursAndMinutes(data.avgUserWatchtime?.value ?? 0) : -1}
          Icon={<Icon name={ICONS.PLAY_SQUARE} color={statsColors.from} size={16} />}
        />
        <StatsCard
          loading={isLoadingSessionStat(ESessionStatKeys.averageProjectRating)}
          title="Rating"
          tooltip={'Average combined rating of project sessions.'}
          value={isPublished ? (data[ESessionStatKeys.averageProjectRating]?.value ?? -1) : -1}
          Icon={<Icon name={ICONS.STAR_2} color={statsColors.from} size={16} />}
        />
        <OptionalComponent display={channelType === ChannelType.Webinar}>
          <StatsCard
            loading={isLoadingSessionStat(ESessionStatKeys.shares)}
            title="Shares"
            tooltip="Total unique shares per session per user"
            value={isPublished ? (data[ESessionStatKeys.shares]?.value ?? -1) : -1}
            Icon={<Icon name={ICONS.ARROW_BIG_TURN_RIGHT} color={statsColors.from} size={16} />}
          />
        </OptionalComponent>
        <OptionalComponent display={channelType === ChannelType.Townhall}>
          <StatsCard
            loading={isLoadingSessionStat(ESessionStatKeys.interactions)}
            title="Interactions"
            tooltip="Page views."
            value={isPublished ? (data[ESessionStatKeys.interactions]?.value ?? -1) : -1}
            Icon={<IconFilterAltGradient from={statsColors.from} to={statsColors.to} />}
          />
        </OptionalComponent>
      </Container>
    </Container>
  );
};

export default SessionStatsRow;
